<template>
    <div class="space-y-6">
        <!-- Page Header -->
        <div class="text-center mb-8">
            <!-- Icon -->
            <div class="mx-auto w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mb-4">
                <svg class="w-8 h-8 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
            </div>

            <h1 class="text-3xl font-bold text-secondary-900 dark:text-white mb-2">
                Two-Factor Authentication
            </h1>
            <p class="text-secondary-600 dark:text-secondary-400">
                {{ two_fa_method === 'email'
                    ? 'Enter the code sent to your email'
                    : 'Enter your authentication code to continue' }}
            </p>
        </div>

        <!-- Tab Selection -->
        <div class="flex space-x-2 mb-6 bg-secondary-100 dark:bg-secondary-900/50 p-1 rounded-lg">
            <button
                @click="activeTab = 'code'"
                :class="[
                    'flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors',
                    activeTab === 'code'
                        ? 'bg-white dark:bg-secondary-800 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
                ]"
                type="button"
            >
                <svg v-if="two_fa_method === 'email'" class="inline-block w-4 h-4 mr-1 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <svg v-else class="inline-block w-4 h-4 mr-1 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {{ two_fa_method === 'email' ? 'Email Code' : 'Authenticator App' }}
            </button>
            <button
                @click="activeTab = 'recovery'"
                :class="[
                    'flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors',
                    activeTab === 'recovery'
                        ? 'bg-white dark:bg-secondary-800 text-primary-600 dark:text-primary-400 shadow-sm'
                        : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
                ]"
                type="button"
            >
                <svg class="inline-block w-4 h-4 mr-1 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                </svg>
                Recovery Code
            </button>
        </div>

        <!-- Authenticator Code Form -->
        <form v-if="activeTab === 'code'" @submit.prevent="submitCode" class="space-y-6">
            <!-- Code Input -->
            <div>
                <label for="code" class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Authentication Code
                </label>
                <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg class="h-5 w-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                        </svg>
                    </div>
                    <input
                        id="code"
                        ref="codeInput"
                        v-model="codeForm.code"
                        type="text"
                        inputmode="numeric"
                        pattern="[0-9]*"
                        maxlength="6"
                        autocomplete="one-time-code"
                        required
                        :class="[
                            'input pl-10 text-center text-2xl tracking-widest font-mono',
                            codeForm.errors?.code ? 'border-danger-300 dark:border-danger-600 focus:border-danger-500 focus:ring-danger-500' : ''
                        ]"
                        placeholder="000000"
                        @input="filterNumeric"
                    />
                </div>
                <p v-if="codeForm.errors?.code" class="mt-2 text-sm text-danger-600 dark:text-danger-400">
                    {{ codeForm.errors.code }}
                </p>
                <p v-else class="mt-2 text-sm text-secondary-500 dark:text-secondary-500">
                    {{ two_fa_method === 'email'
                        ? 'Enter the 6-digit code sent to your email'
                        : 'Enter the 6-digit code from your authenticator app' }}
                </p>
            </div>

            <!-- Remember Device Checkbox -->
            <div class="flex items-center">
                <input
                    id="remember_device"
                    v-model="codeForm.remember_device"
                    type="checkbox"
                    class="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 dark:border-secondary-600 rounded cursor-pointer"
                />
                <label
                    for="remember_device"
                    class="ml-2 block text-sm text-secondary-700 dark:text-secondary-300 cursor-pointer select-none"
                >
                    Remember this device for 14 days
                </label>
            </div>

            <!-- Submit Button -->
            <button
                type="submit"
                :disabled="codeForm.processing || codeForm.code.length !== 6"
                class="w-full flex items-center justify-center px-4 py-2.5 bg-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
                <svg
                    v-if="codeForm.processing"
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-if="codeForm.processing">Verifying...</span>
                <span v-else>Verify Code</span>
            </button>
        </form>

        <!-- Recovery Code Form -->
        <form v-if="activeTab === 'recovery'" @submit.prevent="submitRecovery" class="space-y-6">
            <!-- Recovery Code Input -->
            <div>
                <label for="recovery_code" class="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Recovery Code
                </label>
                <div class="relative">
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg class="h-5 w-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <input
                        id="recovery_code"
                        ref="recoveryInput"
                        v-model="recoveryForm.recovery_code"
                        type="text"
                        autocomplete="off"
                        required
                        :class="[
                            'input pl-10 font-mono',
                            recoveryForm.errors?.recovery_code ? 'border-danger-300 dark:border-danger-600 focus:border-danger-500 focus:ring-danger-500' : ''
                        ]"
                        placeholder="xxxx-xxxx-xxxx-xxxx"
                    />
                </div>
                <p v-if="recoveryForm.errors?.recovery_code" class="mt-2 text-sm text-danger-600 dark:text-danger-400">
                    {{ recoveryForm.errors.recovery_code }}
                </p>
                <p v-else class="mt-2 text-sm text-secondary-500 dark:text-secondary-500">
                    Enter one of your recovery codes
                </p>
            </div>

            <!-- Warning Message -->
            <div class="p-4 bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg">
                <div class="flex items-start space-x-3">
                    <svg class="w-5 h-5 text-warning-600 dark:text-warning-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                    </svg>
                    <p class="text-sm text-warning-800 dark:text-warning-200">
                        Each recovery code can only be used once. Make sure to generate new codes after using this one.
                    </p>
                </div>
            </div>

            <!-- Submit Button -->
            <button
                type="submit"
                :disabled="recoveryForm.processing || !recoveryForm.recovery_code"
                class="w-full flex items-center justify-center px-4 py-2.5 bg-primary-600 hover:bg-primary-700 dark:bg-primary-600 dark:hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            >
                <svg
                    v-if="recoveryForm.processing"
                    class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span v-if="recoveryForm.processing">Verifying...</span>
                <span v-else>Use Recovery Code</span>
            </button>
        </form>

        <!-- Divider -->
        <div class="relative my-6">
            <div class="absolute inset-0 flex items-center">
                <div class="w-full border-t border-secondary-200 dark:border-secondary-700"></div>
            </div>
            <div class="relative flex justify-center text-sm">
                <span class="px-2 bg-white dark:bg-secondary-800 text-secondary-500 dark:text-secondary-400">
                    or
                </span>
            </div>
        </div>

        <!-- Back to Login -->
        <Link
            href="/login"
            class="block w-full text-center px-4 py-2.5 border border-secondary-300 dark:border-secondary-600 rounded-lg text-secondary-700 dark:text-secondary-300 hover:bg-secondary-50 dark:hover:bg-secondary-800 transition-colors font-medium"
        >
            <svg class="inline-block w-4 h-4 mr-2 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Login
        </Link>

        <!-- Help Section -->
        <div class="mt-8 p-4 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg">
            <div class="flex items-start space-x-3">
                <svg class="w-5 h-5 text-info-600 dark:text-info-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
                <div class="text-sm">
                    <p class="text-info-800 dark:text-info-200 font-medium mb-1">
                        {{ two_fa_method === 'email' ? "Didn't receive the code?" : 'Lost access to your authenticator?' }}
                    </p>
                    <p class="text-info-700 dark:text-info-300">
                        <template v-if="two_fa_method === 'email'">
                            Check your spam folder or use a recovery code. Need help? Contact support at
                        </template>
                        <template v-else>
                            Use a recovery code or contact support at
                        </template>
                        <a href="mailto:support@marginresolve.com" class="underline hover:text-info-900 dark:hover:text-info-100">
                            support@marginresolve.com
                        </a>
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, nextTick, watch } from 'vue';
import { Link, router } from '@inertiajs/vue3';
import axios from 'axios';

// Props
const props = defineProps({
    user_id: {
        type: Number,
        required: true,
    },
    two_fa_method: {
        type: String,
        default: 'email',
    },
});

// Active tab
const activeTab = ref('code');

// Form state
const codeForm = ref({
    code: '',
    remember_device: false,
    processing: false,
    errors: {},
});

const recoveryForm = ref({
    recovery_code: '',
    processing: false,
    errors: {},
});

// Refs
const codeInput = ref(null);
const recoveryInput = ref(null);

// Filter numeric input for code
const filterNumeric = (event) => {
    codeForm.value.code = codeForm.value.code.replace(/[^0-9]/g, '');
};

// Submit authenticator code
const submitCode = async () => {
    if (codeForm.value.code.length !== 6) {
        return;
    }

    codeForm.value.processing = true;
    codeForm.value.errors = {};

    try {
        const response = await axios.post('/api/auth/verify-2fa', {
            user_id: props.user_id,
            code: codeForm.value.code,
            remember_device: codeForm.value.remember_device,
        });

        const data = response.data;

        // Check if company selection is needed
        if (data.requires_company_selection) {
            router.visit('/select-company');
        } else {
            router.visit('/dashboard');
        }
    } catch (error) {
        // Handle error
        if (error.response) {
            const message = error.response.data.message || 'Invalid code. Please try again.';
            codeForm.value.errors.code = message;
        } else {
            codeForm.value.errors.code = 'An error occurred. Please try again.';
        }

        // Clear code on error
        codeForm.value.code = '';

        if (codeInput.value) {
            codeInput.value.focus();
        }
    } finally {
        codeForm.value.processing = false;
    }
};

// Submit recovery code
const submitRecovery = async () => {
    if (!recoveryForm.value.recovery_code) {
        return;
    }

    recoveryForm.value.processing = true;
    recoveryForm.value.errors = {};

    try {
        const response = await axios.post('/api/auth/two-factor-recovery', {
            user_id: props.user_id,
            recovery_code: recoveryForm.value.recovery_code,
        });

        const data = response.data;

        // Check if company selection is needed
        if (data.requires_company_selection) {
            router.visit('/select-company');
        } else {
            router.visit('/dashboard');
        }
    } catch (error) {
        // Handle error
        if (error.response) {
            const message = error.response.data.message || 'Invalid recovery code. Please try again.';
            recoveryForm.value.errors.recovery_code = message;
        } else {
            recoveryForm.value.errors.recovery_code = 'An error occurred. Please try again.';
        }

        // Clear recovery code on error
        recoveryForm.value.recovery_code = '';

        if (recoveryInput.value) {
            recoveryInput.value.focus();
        }
    } finally {
        recoveryForm.value.processing = false;
    }
};

// Watch tab changes and focus appropriate input
watch(activeTab, (newTab) => {
    nextTick(() => {
        if (newTab === 'code' && codeInput.value) {
            codeInput.value.focus();
        } else if (newTab === 'recovery' && recoveryInput.value) {
            recoveryInput.value.focus();
        }
    });
});

// Auto-focus on mount
onMounted(() => {
    if (codeInput.value) {
        codeInput.value.focus();
    }
});
</script>

<style scoped>
/* Smooth transitions */
* {
    transition-property: color, background-color, border-color, opacity;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    transition-duration: 150ms;
}
</style>
